import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, MessageCircle } from "lucide-react";
import LegalShell from "./LegalShell";

const ACCENT = "#F5A623";

export default function FAQ() {
  const [openQuestions, setOpenQuestions] = useState(new Set());

  const faqs = [
    {
      question: "How does the Pagent ranking system work?",
      answer:
        "Pagent uses a matchmaking ranking system (MMR). You get matched against creators at a similar level, compete, and the community votes on the winner. Win and your score rises. Lose and it drops. As your ranking climbs, your profile gets more visibility, so the strongest performers naturally reach the biggest audiences.",
    },
    {
      question: "Is Pagent free to download?",
      answer: "Yes. Pagent is free on iOS and Android.",
    },
    {
      question: "How do I become famous singing?",
      answer:
        "Pagent gives singers a direct route to building a real audience. You enter head-to-head singing contests, the community votes on who wins, and every win pushes your ranking higher. The higher you rank, the more people discover your profile. It is not about luck or posting at the right time. It is about how good you actually are.",
    },
    {
      question: "What is the best app to get discovered as a singer?",
      answer:
        "Pagent is built specifically for singers looking to get discovered. Unlike TikTok or Instagram where visibility depends on an algorithm, Pagent ranks you based on contest results. The better you perform, the more prominently your profile appears to new listeners.",
    },
    {
      question: "Is there an app for talent contests?",
      answer:
        "Yes. Pagent is a free talent contest app available on iOS and Android. Singers, dancers, fashion creators and more compete in real-time head-to-head matchups, with the community voting to decide who wins each round.",
    },
    {
      question: "How do I get more followers as a dancer?",
      answer:
        "On Pagent, followers come from ranking, not from posting at the right time. Enter dancing contests, win rounds, and your rank climbs. Higher-ranked creators get more visibility across the app, which means a growing audience that found you because of your talent.",
    },
    {
      question: "How do young creators get famous online?",
      answer:
        "Pagent is designed for exactly this. Rather than hoping a platform algorithm notices you, you compete directly against other creators at your level. Wins build your ranking, your ranking builds your visibility, and your visibility builds your audience.",
    },
    {
      question: "What app can I use to enter singing contests online?",
      answer:
        "Pagent. You can enter head-to-head singing contests or submit entries to open contests, which regularly offer prizes including cash, brand partnerships and record deals. Free to download on iOS and Android.",
    },
  ];

  const toggleQuestion = (index) => {
    setOpenQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  return (
    <LegalShell title="Frequently Asked Questions">
      <div className="space-y-3">
        {faqs.map((faq, index) => {
          const isOpen = openQuestions.has(index);
          return (
            <div
              key={index}
              className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/60 transition-all"
            >
              <button
                onClick={() => toggleQuestion(index)}
                className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-white/5"
              >
                <h3 className="pr-4 text-lg font-semibold text-white">
                  {faq.question}
                </h3>
                <ChevronDown
                  className={`h-5 w-5 flex-shrink-0 text-yellow-400 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`grid transition-all duration-200 ${
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-4 pt-2">
                    <p className="leading-relaxed text-zinc-300">{faq.answer}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="mt-12 rounded-2xl border border-yellow-500/20 bg-yellow-500/10 p-8">
          <div className="space-y-4 text-center">
            <MessageCircle className="mx-auto h-12 w-12 text-yellow-400" />
            <h2 className="text-2xl font-bold text-white">
              Still have questions?
            </h2>
            <p className="text-zinc-300">
              Can't find the answer you're looking for? Our support team is here
              to help.
            </p>
            <Link
              to="/support"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black"
              style={{ backgroundColor: ACCENT }}
            >
              <MessageCircle className="h-4 w-4" />
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </LegalShell>
  );
}
